
import img0 from './Images/Okta_SAML1.png';
import img1 from './Images/Okta_SAML2.png';
import img2 from './Images/Okta_SAML3.png';
import img3 from './Images/Okta_SAML4.png';
import img4 from './Images/Okta_SAML5.png';
  
const content = `# Okta Integration Using SAML
OnData supports the integration with Identity and Access Management (IAM) systems for user authentication and authorization, based on the industry standard 
protocols, including SAML 2.0 and OpenID Connect (OIDC) protocols. The purpose of this document is to detail the process of configuring OnData and Okta using 
the SAML 2.0 protocol, so that the users can login to OnData with their Okta accounts.

### 1. Prerequisite
a. An Okta user account with at least one of the following roles: Super Administrator, or Application Administrator  
b. An OnData account with the System Administrator role  

### 2. Okta Configuration
a. Login to the Okta Admin Console, go to “Applications” -> “Applications”  
b. Click “Create App Integration”  
c. Select “SAML 2.0” as the Sign-in method  
d. Click “Next”  
e. Under “General Settings”, use “OnData Portal” as the “App name” and optionally the OnData logo as the “App logo”. Leave the other options as default  
f. Click “Next”  

![Create New Application](${ img0 })

g. Under “SAML Settings”, copy the "SAML Assertion Consumer Service URL" from the OnData Portal to complete the “Single sign-on URL” field. Below are the steps to find 
the "SAML Assertion Consumer Service URL":  
  * Login to the OnData Portal as a System Administrator, select the "Settings" icon from the top right corner, and select "Organization" from the dropdown menu  
  * Select the "SSO Configuration" tab  
  * Toggle the switch to "Enable Single Sign-ON (SSO)"  
  * Under the "Protocol" choice, select the "SAML 2.0" option and copy the "SAML Assertion Consumer Service URL". And it should 
  be: https://api.ondatahub.com/users/api/sso/oidc/consumer  

h. Enter the “Audience URI”: users.ondatahub.com  
i. Choose “EmailAddress” as the “Name ID format”, and “Email” as the “Application username”  
j. Under “Attribute Statements”, enter “Roles” as the “Name”, and “appuser.Roles” as the “Value”  
k. Leave all other fields with default values and click “Next”  

![SAML Settings](${ img1 })

l. There is no need to enter any information for this page. Click “Finish” to complete the wizard  
m. Once the application is created, go to the “Sign On” tab and click “More details” to view the detailed information
- Copy the “Sign on URL”, which is needed to configure the “Sign In URL” field at the OnData side
- Copy the “Issuer”, which is needed to configure the “Service Provider Entity ID” field at the OnData side
- Download a copy of the “Signing Certificate”, which is needed to configure the “Identity Provider X.509 Certificate” field at the OnData side  

n. Go to “Directory” -> “Profile Editor” and select “Apps” under the “Users” tab  
o. Click the “OnData Portal User” profile to show the “Profile Editor”  
p. Click “Add Attribute” to add an attribute with the following options  
- Data type: string array
- Display name: OnData App Roles
- Variable name: Roles
- Enum: check the “Define enumerated list of values” option, enter “System Administrator” as the “Display name”, and “SystemAdministrator” as the “Value”
- Attribute required: check the “Yes” option
- Attribute type: select the “Group” option
- Group Priority: select the "Combine values across groups" option
- Leave all other fields as default and click “Save”

 ![App Role Attribute](${ img2 })

q. Go to “Directory” -> “Groups” and select “Add group” to add new group  
r. Use the group name of “OnData Admins” (or whatever names preferred) and click “Save”  
s. Click the newly added "OnData Admins" group, and under the “People” tab, assign one or more users to this group who will be the OnData system administrators  
t. Under the “Application” tab, click “Assign applications”  
u. Find the “OnData Portal” application and click “Assign”  
v. Select “System Administrator” for the “OnData App Roles”  
w. Click “Save and Go Back” to save the assignment  
x. Click “Done” to complete the assignment  

 ![Assign Application](${ img3 })


### 3. OnData Configuration
a. Login to OnData as a local System Administrator, select the Settings icon from the top right corner, and select Organization from the dropdown menu  
b. Select the SSO Configuration tab  
c. Toggle the switch to “Enable Single Sign-ON (SSO)”  
d. Under the Protocol choice, select the “SAML 2.0” option  
e. Fill out the “Service Provider Entity ID” field with the value of the “Issuer” field from Okta  
f. Fill out the “Sign In URL” field with the value of the “Sign on URL” field from Okta  
g. Upload the “Signing Certificate” file downloaded from Okta to the “Identity Provider X.509 Certificate” field  
h. Click on the “here” link in the sentence “You can download the related OnData signing certificate from here” to download the “ondata_slo.pem” file  
- Go back to the “OnData Portal” application at Okta, find the “SAML Settings” section under the “General” tab, and click “Edit” and then click “Next”
- Click “Show Advanced Settings” in the “SAML Settings” section, click “Browse files…” next to “Signature Certificate”, and select and upload the “ondata_slo.pem” file
- Check the option "Allow application to initiate Single Logout" next to "Enable Single Logout"
- Enter https://us.ondatahub.com for the "Single Logout URL" field and users.ondatahub.com for the "SP Issuer" field
- Click “Next” and “Finish” to complete
- Go to the “Sign On” tab and click “More details” to view the detailed information
- Copy the “Single Logout URL” and use it to configure the “On Logout, Sign Out IdP at URL” field at the OnData side

i. Click on the “Update SSO Configuration” button to update the SSO settings  
j. Log out of OnData and login again with a user’s organization email address who has been granted access to the OnData Portal application in 
Step 2 – Okta Configuration above  
k. You should be redirected to the Okta for login. After a successful login, you will be redirected back to the OnData Portal  

 ![OnData SSO Configuration](${ img4 })
   
   
`
  
export default content;
